/**
 * PC Skin - Base Component Factory
 *
 * Creates PC-themed color-specific base components using a factory pattern.
 * Each variant is pre-configured with its color scheme and default pawn component.
 */

import React from 'react';
import type { ColoredBaseProps } from '../../constants/types';
import PcBaseComponent from './PcBaseComponent';
import { PC_BASE_COLOR_SCHEMES, type PcBaseColorScheme } from './constants/baseColorSchemes';
import { RedPawn, BluePawn, YellowPawn, GreenPawn } from '../../pawns/pc/pawnFactory';
import type { PawnProps } from '../../constants/types';

function createPcBase(
  color: string,
  colorScheme: PcBaseColorScheme,
  PawnComponent: React.ComponentType<PawnProps>
) {
  const displayName = `Pc${color.charAt(0).toUpperCase() + color.slice(1)}Base`;

  const Base = React.memo(function PcBase(props: ColoredBaseProps) {
    return (
      <PcBaseComponent
        {...props}
        colorScheme={colorScheme}
        PawnComponent={props.PawnComponent ?? PawnComponent}
      />
    );
  });

  Base.displayName = displayName;
  return Base;
}

export const RedBase = createPcBase('red', PC_BASE_COLOR_SCHEMES.red, RedPawn);
export const BlueBase = createPcBase('blue', PC_BASE_COLOR_SCHEMES.blue, BluePawn);
export const YellowBase = createPcBase('yellow', PC_BASE_COLOR_SCHEMES.yellow, YellowPawn);
export const GreenBase = createPcBase('green', PC_BASE_COLOR_SCHEMES.green, GreenPawn);

export type { ColoredBaseProps as BlueBaseProps };
export type { ColoredBaseProps as RedBaseProps };
export type { ColoredBaseProps as GreenBaseProps };
export type { ColoredBaseProps as YellowBaseProps };
